// Core lib dependencies.
import React from 'react';

// Shared utils for Display Items.
import * as utils from './utils.jsx';
import { sanitizeHTML } from '../../utils/string.js';

import { ItemCouponCode, ItemCouponMessage } from './items/item-components';

// Other component dependencies.
import { Button } from '../buttons.jsx';

// Misc dependencies.
import { capitalize, getLabel } from '../../utils/string';
import { getDateTime } from '../../utils/datetime';
import { assetFromRef } from '../../../legacy/utils/utils.js';

/**
 * <pre>
 * Display Item > Reward related React components.
 *
 * </pre>
 *
 * @author Various
 * @namespace Common.Components.DisplayItems.Rewards
 */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Components */

/**
 * Inline Reward Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Rewards
 */
class RewardInline extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const item = this.props.item;

    const _badgeThumb = getCouponBadge(item),
      _mainContent = getCouponHeading(item),
      _couponCode = getItemCouponCode(item),
      _couponMessage = getItemCouponMessage(item),
      _couponCTA = getItemCouponCTA(item);

    const innerStyle = {};

    const displayItemModel = {
      className: getCouponClasses(this.props.item, this.props.templateOpts).join(' '),
      onClick:   () => {
        this.props.onClick(this.props.item);
      },
    };

    if ('function' === typeof this.props.onClick) {
      displayItemModel.role = 'button';
    }
    return (
      <div {...displayItemModel}>
        <div className="xItemInner" style={innerStyle}>
          {_badgeThumb}
          {_mainContent}
          {_couponCode}
          {_couponMessage}
          {_couponCTA}
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 */
RewardInline.propTypes = {};

/**
 * React default props.
 *
 * @type {Object}
 */
RewardInline.defaultProps = {
  templateOpts: {
    variant: 'inline',
  },
};

export { RewardInline };

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private utility functions */

/**
 * Get list of coupon specific classes.
 *
 * @private
 *
 * @param {object} item - the display item/coupon reward
 * @param {object} template - the template object
 */
function getCouponClasses(item, template) {
  const classes = ['xItem'];

  if (template) {
    if (template.variant) classes.push('xVariant' + capitalize(template.variant));
  }

  classes.push('xType' + capitalize(item.type));

  classes.push(item.isEligible ? 'xStatusUnlocked' : 'xStatusLocked');
  classes.push(item.isAvailable ? 'xStatusAvailable' : 'xStatusUnavailable');
  classes.push(item.isPrivate ? 'xStatusPrivate' : '');
  classes.push(item.isEntitled ? 'xStatusGranted' : '');

  return classes;
}

/**
 * Returns the coupon's badge if set.
 *
 * @private
 *
 * @param {object} item - coupon display item
 * @return {React} wrapped badge image
 */
function getCouponBadge(item) {
  /**
   * Deriving the style property. If it is defined, it should be like “size=cover”. In case
   * of native image size, it uses the “size=auto”, which does not exist as object-fit value,
   * so I replace it to ‘none’.
   */
  const sizingSetting = !!item.formatting && !!item.formatting.size ? item.formatting.size : '';
  let _badge,
    _style = {},
    _badgeClasses = ['xMediaContainer', 'xMediaImage', 'xThumbnailAsset'];

  _badgeClasses.push('xType' + item.type);

  /** We use this hacky approach to ensure that the image would be displayed correctly (with respect to
   * sizing property). The <img> tag is hidden (opaque), but we need it to ensure that our div will not
   * be empty. And we use background of the hosting div because of the hosting markup: we render rewards
   * as "display: table" and we cannot just set the height 100% for the image.
   * */
  const backgroundStyles = {};

  const assetData = assetFromRef(item.badgeThumb, NGX.context);
  const assetRef = assetData && assetData.src ? assetData.src : item.badgeThumb;
  // If we have explicitly added sizing for the thumbnail, we will use div with background.
  if (sizingSetting) {
    _badgeClasses.push('xSizedImageBackground');
    _style.objectFit = sizingSetting.replace('auto', 'none');
    _style.opacity = 0;
    backgroundStyles.backgroundImage = `url(${assetRef})`;
    backgroundStyles.backgroundSize = sizingSetting;
    backgroundStyles.backgroundRepeat = 'no-repeat';
    backgroundStyles.backgroundClip = 'content-box';
  }
  // Otherwise (for existing campaigns) we use the image and apply the default object-fit value.
  else {
    _style.objectFit = 'contain';
  }

  if (item.badgeThumb) {
    _badge = (
      <div className={_badgeClasses.join(' ')} style={backgroundStyles}>
        <img src={assetRef} alt={item.heading} style={_style} />
      </div>
    );
  }

  return _badge;
}

/**
 * Returns a coupon component.
 *
 * @private
 *
 * @param item : {object} display item
 * @return {React} Coupon component
 */
function getItemCouponCode(item) {
  let _coupon;

  if (item.userInfo && item.code) {
    _coupon = <ItemCouponCode code={item.code} userInfo={item.userInfo} />;
  }

  return _coupon;
}

/**
 * Returns the coupon message.
 *
 * @private
 *
 * @param item : {object} display item
 * @return {React} Coupon message component
 */
function getItemCouponMessage(item) {
  let _coupon;
  const _statusMessages = {
    available: item.availableStatusMessage,
    granted:   item.grantStatusMessage,
  };

  if (item.userInfo) {
    _coupon = <ItemCouponMessage userInfo={item.userInfo} statusMessages={_statusMessages} />;
  }

  return _coupon;
}

/**
 * Returns the CTA if the correct type of coupon is selected and user is eligible.
 *
 * @private
 *
 * @param {object} item - the reward
 * @returns {React} Button
 */
function getItemCouponCTA(item) {
  let _cta;

  if (item.isEligible && item.target && item.target.strategy == 'strategy.delivery.coupon.link') {
    return getItemCTA(item.target.object, item.target.text);
  }

  return _cta;
}

function getItemCTA(target, text) {
  return (
    <div className="xCTAContainer">
      <Button addClasses="xCTA xOpen" target="_blank" href={target}>
        {text}
      </Button>
    </div>
  );
}

/**
 * Returns the headline/byline for a coupon if provided.
 *
 * @private
 *
 * @param {object} item - reward item
 * @returns {React} wrapped header/byline
 */
function getCouponHeading(item) {
  let _heading, _byline, _cta, _summary;

  if (item.heading) {
    _heading = <h1 className="xTitle xHeading" dangerouslySetInnerHTML={sanitizeHTML(item.heading)} />;
  }

  if (item.byline) {
    _byline = <h2 className="xTitle xByline" dangerouslySetInnerHTML={sanitizeHTML(item.byline)} />;
  }

  if (item.summary) {
    _summary = <p className="xCopy xContentBody" dangerouslySetInnerHTML={sanitizeHTML(item.summary)} />;
  }

  if (item.ctaLink) {
    _cta = getItemCTA(item.ctaLink, item.ctaButtonText);
  }
  const _validDates = getCouponValidDates(item);
  const _redemptionInstructions = getCouponInstructions(item);

  if (_heading || _byline || _summary || _validDates || _redemptionInstructions) {
    return (
      <div className="xCopyContainer xMain">
        {_heading}
        {_byline}
        {_summary}
        {_cta}
        {_validDates}
        {_redemptionInstructions}
      </div>
    );
  }
}

/**
 * Returns the valid to/from dates for a coupon if provided
 *
 * @private
 *
 * @param {object} item - reward item
 * @returns {React} wrapped dates
 */
function getCouponValidDates(item) {
  let _from, _to;

  if (item.validFrom) {
    const fromLabelTemplate = getLabel('coupon.validAfter');
    const fromLabel = fromLabelTemplate.replace('{0}', getDateTime(item.validFrom));
    _from = <p className="xCopy">{fromLabel}</p>;
  }

  if (item.validTo) {
    const toLabelTemplate = getLabel('coupon.validBefore');
    const toLabel = toLabelTemplate.replace('{0}', getDateTime(item.validTo));
    _to = <p className="xCopy">{toLabel}</p>;
  }

  if (_from || _to) {
    return (
      <span>
        {_from}
        {_to}
      </span>
    );
  }
}

/**
 * Returns redemption instructions if provided
 *
 * @private
 *
 * @param {object} item - reward item
 * @returns {React} wrapped instructions
 */
function getCouponInstructions(item) {
  if (item.redemptionInstructions) {
    return <p className="xCopy" dangerouslySetInnerHTML={{ __html: item.redemptionInstructions }} />;
  }
}
